/**
 * Address book encryption (#476).
 *
 * The address book is backed up to the server as an AES-GCM–encrypted VCF blob.
 * The key is derived on the client from the user's own plaintext phone number
 * via PBKDF2, so the server only ever stores opaque ciphertext.
 *
 * The VCF is gzip-compressed before encryption (VCF is very repetitive text, so
 * this shrinks the payload ~5x). A one-byte format tag prefixes the plaintext so
 * decryption knows whether to inflate, and so we can fall back to storing raw
 * bytes on the rare engine without CompressionStream. Compress-then-encrypt is
 * safe here: there is no attacker-chosen data compressed alongside a secret, and
 * the ciphertext length is only visible to the server, which already sees the
 * plaintext length (the scheme is not zero-knowledge against the server).
 *
 * Threat model: this protects the blob against database-at-rest leaks. It is not
 * zero-knowledge against the server itself — the phone-number space is small and
 * the server already holds the OPRF oracle for it, so a determined server could
 * re-derive the key. That matches the guarantee the persisted phone_oprf already
 * provides for the account identity.
 */

import { PhoneLogic } from '@/modules/phone';

// The Compression Streams API is available at runtime (evergreen browsers, iOS
// Safari 16.4+, Node 18+) but isn't in this TypeScript version's DOM lib
// (added in TS 5.1). Declare the minimal surface we use.
declare global {
  class CompressionStream {
    constructor(format: 'gzip' | 'deflate' | 'deflate-raw');
    readonly readable: ReadableStream<Uint8Array>;
    readonly writable: WritableStream<Uint8Array>;
  }
  class DecompressionStream {
    constructor(format: 'gzip' | 'deflate' | 'deflate-raw');
    readonly readable: ReadableStream<Uint8Array>;
    readonly writable: WritableStream<Uint8Array>;
  }
}

// OWASP-recommended floor for PBKDF2-HMAC-SHA256.
const PBKDF2_ITERATIONS = 210_000;
const SALT = new TextEncoder().encode('snowball-address-book-v1');
const NONCE_BYTES = 12; // standard AES-GCM IV length

// One-byte format tag prefixed to the plaintext before encryption.
const FORMAT_RAW = 0x00; // UTF-8 VCF, uncompressed (fallback)
const FORMAT_GZIP = 0x01; // gzip-compressed UTF-8 VCF

/** An encrypted VCF blob, base64-encoded for JSON transport. */
export interface EncryptedBlob {
  /** base64 AES-GCM ciphertext (includes the GCM auth tag). */
  ciphertext: string;
  /** base64 AES-GCM nonce/IV. */
  nonce: string;
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function fromBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function deriveKey(normalizedPhone: string): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(normalizedPhone),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: SALT,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

// Deriving the key is deliberately slow (PBKDF2), so cache it in memory for the
// session, keyed by normalized phone. The key is never persisted.
let cached: { phone: string; key: Promise<CryptoKey> } | null = null;

function getKey(phone: string): Promise<CryptoKey> {
  // PhoneLogic.normalize strips formatting and reduces to the last 10 digits, so
  // the derived key is stable regardless of how the number was entered.
  const normalized = PhoneLogic.normalize(phone);
  if (!cached || cached.phone !== normalized) {
    cached = { phone: normalized, key: deriveKey(normalized) };
  }
  return cached.key;
}

/** Run bytes through a (De)CompressionStream and collect the result. */
async function streamBytes(
  data: Uint8Array,
  stream: CompressionStream | DecompressionStream
): Promise<Uint8Array> {
  const writer = stream.writable.getWriter();
  // Don't await write/close: the Response below drains `readable` concurrently,
  // so awaiting here first would deadlock on backpressure.
  void writer.write(data);
  void writer.close();
  return new Uint8Array(await new Response(stream.readable).arrayBuffer());
}

/** Prefix a payload with its one-byte format tag. */
function withFormat(tag: number, payload: Uint8Array): Uint8Array {
  const out = new Uint8Array(payload.length + 1);
  out[0] = tag;
  out.set(payload, 1);
  return out;
}

/** Compress + tag the VCF for encryption; falls back to raw if unsupported. */
async function packPlaintext(vcf: string): Promise<Uint8Array> {
  const utf8 = new TextEncoder().encode(vcf);
  if (typeof CompressionStream === 'undefined') {
    return withFormat(FORMAT_RAW, utf8);
  }
  const gzipped = await streamBytes(utf8, new CompressionStream('gzip'));
  return withFormat(FORMAT_GZIP, gzipped);
}

/** Reverse {@link packPlaintext}: read the format tag and inflate if needed. */
async function unpackPlaintext(bytes: Uint8Array): Promise<string> {
  const tag = bytes[0];
  const body = bytes.subarray(1);
  if (tag === FORMAT_GZIP) {
    const inflated = await streamBytes(body, new DecompressionStream('gzip'));
    return new TextDecoder().decode(inflated);
  }
  // FORMAT_RAW (and any unknown tag, treated as raw UTF-8).
  return new TextDecoder().decode(body);
}

/** Encrypt a VCF string with a key derived from the user's phone number. */
export async function encryptVcf(
  phone: string,
  vcf: string
): Promise<EncryptedBlob> {
  const key = await getKey(phone);
  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: nonce },
      key,
      await packPlaintext(vcf)
    )
  );
  return { ciphertext: toBase64(ciphertext), nonce: toBase64(nonce) };
}

/** Decrypt a VCF blob previously produced by {@link encryptVcf}. */
export async function decryptVcf(
  phone: string,
  blob: EncryptedBlob
): Promise<string> {
  const key = await getKey(phone);
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromBase64(blob.nonce) },
    key,
    fromBase64(blob.ciphertext)
  );
  return unpackPlaintext(new Uint8Array(plaintext));
}

/** Clear the cached derived key (e.g. on sign-out or phone change). */
export function clearAddressBookKeyCache(): void {
  cached = null;
}
